import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X } from "lucide-react";
import KaiChat from "@/pages/KaiChat";

/**
 * Floating Kai launcher with an integrated popup chat panel.
 * - Hidden on /kai (full-page experience) and /admin/* routes
 * - Popup shares the same localStorage session (`kai_session_v2`) as /kai,
 *   so users verified in the popup stay verified on the full page and vice versa.
 * - Mobile: full-screen takeover. Desktop: corner-anchored 380×620 panel.
 */
const KaiLauncher = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Lock background scroll on mobile when popup is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    if (window.matchMedia("(max-width: 640px)").matches) {
      document.body.style.overflow = "hidden";
    }
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (pathname.startsWith("/kai") || pathname.startsWith("/admin")) return null;

  return (
    <>
      <AnimatePresence>
        {!open && (
          <motion.button
            key="launcher-btn"
            onClick={() => setOpen(true)}
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ duration: 0.25 }}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            data-testid="kai-floating-launcher"
            aria-label="Open Kai — your GLP-1 coach"
            className="fixed z-[60] flex items-center gap-2 rounded-full border-2 border-white"
            style={{
              bottom: "max(1.25rem, env(safe-area-inset-bottom, 0.5rem))",
              right: "1.25rem",
              padding: "12px 18px",
              backgroundColor: "#2D6A4F",
              color: "white",
              fontWeight: 600,
              fontSize: "14px",
              boxShadow: "0 10px 30px rgba(45, 106, 79, 0.35), 0 4px 10px rgba(0,0,0,0.1)",
            }}
          >
            <span className="relative flex items-center justify-center w-6 h-6 rounded-full bg-white/20">
              <span className="absolute inset-0 rounded-full bg-white/30 animate-ping" />
              <Sparkles className="w-4 h-4 relative" />
            </span>
            <span className="hidden sm:inline">Ask Kai</span>
            <span className="sm:hidden">Kai</span>
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <>
            {/* Mobile backdrop (desktop has no backdrop — popup floats inline) */}
            <motion.div
              key="kai-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-[55] bg-black/30 sm:hidden"
              aria-hidden="true"
            />
            <motion.div
              key="kai-popup"
              initial={{ opacity: 0, scale: 0.96, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 20 }}
              transition={{ duration: 0.25 }}
              data-testid="kai-popup-panel"
              role="dialog"
              aria-label="Kai chat"
              className="fixed z-[60] bg-white shadow-2xl overflow-hidden
                         inset-0 sm:inset-auto
                         sm:bottom-6 sm:right-6
                         sm:w-[380px] sm:h-[620px] sm:max-h-[calc(100vh-3rem)]
                         sm:rounded-2xl sm:border sm:border-[#E8E4DC]"
              style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}
            >
              {/* Mobile top bar with close (desktop close is inside KaiChat header) */}
              <button
                onClick={() => setOpen(false)}
                aria-label="Close Kai"
                data-testid="kai-popup-mobile-close"
                className="absolute top-3 right-3 z-10 sm:hidden p-2 rounded-lg bg-white/90 shadow"
                style={{ color: "#3D3D3D" }}
              >
                <X className="w-4 h-4" />
              </button>
              <KaiChat variant="popup" onClose={() => setOpen(false)} />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default KaiLauncher;
